import React, { useState, useEffect } from 'react'
import {
  Activity, AlertTriangle, CheckCircle, XCircle, Database,
  Server, HardDrive, Cpu, MemoryStick, RefreshCw, TrendingUp,
  Clock, Zap, Users, FileText
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface HealthComponent {
  status: 'healthy' | 'warning' | 'unhealthy' | 'unknown' | 'unavailable'
  [key: string]: any
}

interface SystemHealth {
  timestamp: string
  status: 'healthy' | 'warning' | 'degraded'
  components: {
    database: HealthComponent
    redis: HealthComponent
    system: HealthComponent
    background_jobs: HealthComponent
    api: HealthComponent
  }
}

interface SystemStats {
  users: {
    total_users: number
    parents: number
    kids: number
    active_week: number
  }
  chores: {
    total_chores: number
    active_chores: number
    categories: number
  }
  assignments: {
    pending: number
    completed: number
    verified: number
    created_today: number
    completed_today: number
  }
  rewards: {
    total_rewards: number
    active_rewards: number
    total_points_cost: number
  }
  points: {
    total_in_system: number
    avg_per_user: number
    max_points: number
  }
}

export const SystemHealthComponent: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null)
  const [stats, setStats] = useState<SystemStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Background refreshes fail quietly; only the first load reports an error.
  const loadData = async (background = false) => {
    try {
      const [healthData, statsData] = await Promise.all([
        apiClient.get('/api/system/health'),
        apiClient.get('/api/system/stats')
      ])

      setHealth(healthData)
      setStats(statsData)
      setLastRefresh(new Date())
    } catch (error) {
      console.error('Failed to load system data:', error)
      if (!background) toast.error('Failed to load system health data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => loadData(true), 30000)
    return () => clearInterval(interval)
  }, [])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />
      case 'unhealthy':
        return <XCircle className="w-5 h-5 text-red-500" />
      case 'unavailable':
        return <XCircle className="w-5 h-5 text-gray-400" />
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-50 border-green-200'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200'
      case 'unhealthy':
        return 'bg-red-50 border-red-200'
      case 'unavailable':
        return 'bg-gray-50 border-gray-200'
      default:
        return 'bg-gray-50 border-gray-200'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
          <p className="text-gray-600">Real-time system monitoring and statistics</p>
        </div>
        <button
          onClick={() => loadData()}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      {health && (
        <div className={`p-6 rounded-lg border-2 ${getStatusColor(health.status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getStatusIcon(health.status)}
              <div>
                <h3 className="text-lg font-semibold capitalize">{health.status} Status</h3>
                <p className="text-sm text-gray-600">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">
                {health.status === 'healthy' ? '' : health.status === 'warning' ? '�' : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Component Health */}
      {health && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Database */}
          <div className={`p-4 rounded-lg border ${getStatusColor(health.components.database.status)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Database className="w-5 h-5" />
                <h4 className="font-semibold">Database</h4>
              </div>
              {getStatusIcon(health.components.database.status)}
            </div>
            <div className="space-y-1 text-sm">
              <div>Users: {health.components.database.user_count}</div>
              <div>Size: {health.components.database.size_mb} MB</div>
              <div>Connections: {health.components.database.active_connections}</div>
            </div>
          </div>

          {/* Redis */}
          <div className={`p-4 rounded-lg border ${getStatusColor(health.components.redis.status)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5" />
                <h4 className="font-semibold">Redis</h4>
              </div>
              {getStatusIcon(health.components.redis.status)}
            </div>
            <div className="space-y-1 text-sm">
              {health.components.redis.version && (
                <div>Version: {health.components.redis.version}</div>
              )}
              {health.components.redis.used_memory_mb && (
                <div>Memory: {health.components.redis.used_memory_mb} MB</div>
              )}
              {health.components.redis.note && (
                <div className="text-gray-500">{health.components.redis.note}</div>
              )}
            </div>
          </div>

          {/* System Resources */}
          <div className={`p-4 rounded-lg border ${getStatusColor(health.components.system.status)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Server className="w-5 h-5" />
                <h4 className="font-semibold">System</h4>
              </div>
              {getStatusIcon(health.components.system.status)}
            </div>
            <div className="space-y-1 text-sm">
              <div>CPU: {health.components.system.cpu_percent}%</div>
              <div>Memory: {health.components.system.memory_percent}%</div>
              <div>Disk: {health.components.system.disk_percent}%</div>
            </div>
          </div>

          {/* Background Jobs */}
          <div className={`p-4 rounded-lg border ${getStatusColor(health.components.background_jobs.status)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <h4 className="font-semibold">Background Jobs</h4>
              </div>
              {getStatusIcon(health.components.background_jobs.status)}
            </div>
            <div className="space-y-1 text-sm">
              <div>Pending: {health.components.background_jobs.pending || 0}</div>
              <div>Failed: {health.components.background_jobs.failed || 0}</div>
              <div>Completed (24h): {health.components.background_jobs.completed_24h || 0}</div>
            </div>
          </div>

          {/* API Performance */}
          <div className={`p-4 rounded-lg border ${getStatusColor(health.components.api.status)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5" />
                <h4 className="font-semibold">API</h4>
              </div>
              {getStatusIcon(health.components.api.status)}
            </div>
            <div className="space-y-1 text-sm">
              <div>Requests (1h): {health.components.api.requests_last_hour || 0}</div>
              {health.components.api.avg_response_time_ms && (
                <div>Avg Response: {health.components.api.avg_response_time_ms}ms</div>
              )}
              {health.components.api.note && (
                <div className="text-gray-500">{health.components.api.note}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* System Statistics */}
      {stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">System Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Users */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Users className="w-5 h-5 text-blue-500" />
                <h4 className="font-medium">Users</h4>
              </div>
              <div className="space-y-1 text-sm">
                <div>Total: {stats.users.total_users}</div>
                <div>Parents: {stats.users.parents}</div>
                <div>Kids: {stats.users.kids}</div>
                <div>Active (7d): {stats.users.active_week}</div>
              </div>
            </div>

            {/* Chores */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <FileText className="w-5 h-5 text-green-500" />
                <h4 className="font-medium">Chores</h4>
              </div>
              <div className="space-y-1 text-sm">
                <div>Total: {stats.chores.total_chores}</div>
                <div>Active: {stats.chores.active_chores}</div>
                <div>Categories: {stats.chores.categories}</div>
              </div>
            </div>

            {/* Assignments */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="w-5 h-5 text-orange-500" />
                <h4 className="font-medium">Assignments</h4>
              </div>
              <div className="space-y-1 text-sm">
                <div>Pending: {stats.assignments.pending}</div>
                <div>Completed: {stats.assignments.completed}</div>
                <div>Verified: {stats.assignments.verified}</div>
                <div>Today: {stats.assignments.created_today}</div>
              </div>
            </div>

            {/* Points */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                <h4 className="font-medium">Points</h4>
              </div>
              <div className="space-y-1 text-sm">
                <div>Total: {stats.points.total_in_system.toLocaleString()}</div>
                <div>Average: {stats.points.avg_per_user}</div>
                <div>Max: {stats.points.max_points.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}